// Sprint agent-stages — "Gestionar estados" dialog (spec D6 / AC2).
//
// Two views inside one shadcn Dialog:
//   * list   — every stage: color dot, name, reorder ↑/↓, edit, delete.
//   * editor — create or rename/recolor a single stage. The swatch grid
//     reuses the EditProjectModal idiom (40×40, --radius, outline ring on
//     the selected swatch) so the two pickers stay visually identical.
//
// Deleting a stage NEVER deletes agents: the confirm body says where its
// agents go ("Sin estado") and the store + backend clear pointers
// transactionally (spec AC2).
//
// `mode="create"` opens straight into the editor — used by the context
// menu's "Nuevo estado…" so a founder can create-and-assign in one flow
// (Sidebar passes `onCreated` to do the assignment).

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Stage } from '../types';
import { useStagesStore } from '../stores/stages.store';
import { useAppStore } from '../stores/app.store';
import { useTranslation } from '../i18n';
import { PALETTE_IDS, isPaletteId, paletteColor, type PaletteId } from '../lib/avatar-defaults';
import { ConfirmDialog } from './ConfirmDialog';
// DS.7 D17 — `ds/DsModal` -> `@ds/overlays/Modal` at `size="sm"` (480 px,
// from the measured `sm:max-w-md`); `@ds/data-input/TextField`'s `label` prop
// replaces `ui/label`. This dialog is ALSO the campaign's first nested pair:
// it opens `ConfirmDialog` on top of itself, which is why D18's layer stack
// exists (one Escape must close the confirm and leave this one open).
import { DsModal } from './ds/DsModal';
import Button from '@ds/actions/Button';
import { DsIconButton } from './ds/DsIconButton';
import TextField from '@ds/data-input/TextField';
import { Icon } from './atoms/Icon';

export interface ManageStagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: 'manage' | 'create';
  onCreated?: (stage: Stage) => void;
}

type View = { kind: 'list' } | { kind: 'editor'; editing: Stage | null };

// Default color for a new stage: first palette id not already used by an
// existing stage (wraps around when all 12 are taken).
function nextFreeColor(stages: Stage[]): PaletteId {
  const used = new Set(stages.map((s) => s.color));
  return PALETTE_IDS.find((id) => !used.has(id)) ?? PALETTE_IDS[stages.length % 12]!;
}

export function ManageStagesDialog({
  open,
  onOpenChange,
  mode = 'manage',
  onCreated,
}: ManageStagesDialogProps) {
  const { t } = useTranslation();
  const stages = useStagesStore((s) => s.stages);
  const createStage = useStagesStore((s) => s.createStage);
  const updateStage = useStagesStore((s) => s.updateStage);
  const reorder = useStagesStore((s) => s.reorder);
  const removeStage = useStagesStore((s) => s.removeStage);
  const agents = useAppStore((s) => s.agents);

  const [view, setView] = useState<View>({ kind: 'list' });
  // The editor view opens on its name field — the `autoFocus` the bespoke
  // `<Input>` carried, expressed through the piece's own prop.
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState<PaletteId>('palette-01');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Stage | null>(null);

  // Reset per open; create-mode jumps straight into the editor.
  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      setView({ kind: 'editor', editing: null });
      setName('');
      setColor(nextFreeColor(useStagesStore.getState().stages));
    } else {
      setView({ kind: 'list' });
    }
    setSaving(false);
    setConfirmDelete(null);
  }, [open, mode]);

  const agentCountByStage = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of Object.values(agents)) {
      if (a.deleted || a.archived || a.stageId == null) continue;
      counts.set(a.stageId, (counts.get(a.stageId) ?? 0) + 1);
    }
    return counts;
  }, [agents]);

  function openEditor(stage: Stage | null) {
    setView({ kind: 'editor', editing: stage });
    setName(stage?.name ?? '');
    setColor(
      stage && isPaletteId(stage.color) ? stage.color : nextFreeColor(stages),
    );
  }

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed || view.kind !== 'editor') return;
    setSaving(true);
    if (view.editing) {
      await updateStage(view.editing.id, { name: trimmed, color });
      setSaving(false);
      setView({ kind: 'list' });
      return;
    }
    const created = await createStage(trimmed, color);
    setSaving(false);
    if (created && mode === 'create') {
      onCreated?.(created);
      onOpenChange(false);
      return;
    }
    setView({ kind: 'list' });
  }

  function move(stage: Stage, dir: -1 | 1) {
    const ids = stages.map((s) => s.id);
    const idx = ids.indexOf(stage.id);
    const target = idx + dir;
    if (idx === -1 || target < 0 || target >= ids.length) return;
    const next = [...ids];
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    void reorder(next);
  }

  return (
    <>
      <DsModal
        open={open}
        onClose={() => onOpenChange(false)}
        size="sm"
        title={
          view.kind === 'editor'
            ? view.editing
              ? t('stages.editTitle')
              : t('stages.createTitle')
            : t('stages.manageTitle')
        }
        initialFocusRef={view.kind === 'editor' ? nameRef : undefined}
        data-testid="manage-stages-dialog"
        footer={
          view.kind === 'list' ? (
            <Button onClick={() => openEditor(null)} data-testid="manage-stages-new">
              <Icon name="Plus" size={14} aria-hidden="true" />
              {t('stages.newStageButton')}
            </Button>
          ) : (
            <div className="flex flex-row justify-end gap-2 w-full">
              <Button
                variant="text"
                onClick={() =>
                  mode === 'create' ? onOpenChange(false) : setView({ kind: 'list' })
                }
                disabled={saving}
                data-testid="stage-editor-cancel"
              >
                {t('stages.cancel')}
              </Button>
              <Button
                onClick={() => void handleSave()}
                disabled={saving || name.trim().length === 0}
                data-testid="stage-editor-save"
              >
                {saving
                  ? t('stages.saving')
                  : view.editing
                    ? t('stages.save')
                    : t('stages.create')}
              </Button>
            </div>
          )
        }
      >
          {view.kind === 'list' ? (
            <>
              <div className="flex flex-col py-1 max-h-96 overflow-y-auto" data-testid="manage-stages-list">
                {stages.length === 0 && (
                  <p className="text-body text-muted-foreground py-3" data-testid="manage-stages-empty">
                    {t('stages.emptyList')}
                  </p>
                )}
                {stages.map((s, idx) => {
                  const hex = isPaletteId(s.color) ? paletteColor(s.color).bg : undefined;
                  return (
                    <div
                      key={s.id}
                      className="flex items-center gap-2.5 py-1.5 min-h-touch-target"
                      data-testid={`manage-stage-row-${s.id}`}
                    >
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                        style={hex ? { backgroundColor: hex } : undefined}
                        aria-hidden="true"
                      />
                      <span className="flex-1 min-w-0 truncate text-body">{s.name}</span>
                      {/* eslint-disable-next-line i18n-enforce/no-literal-strings-in-jsx -- decorative count */}
                      <span className="text-caption text-muted-foreground/70">
                        {agentCountByStage.get(s.id) ?? 0}
                      </span>
                      <StageRowButton
                        icon="ArrowUp"
                        disabled={idx === 0}
                        label={t('stages.moveUp')}
                        onClick={() => move(s, -1)}
                        testid={`manage-stage-up-${s.id}`}
                      />
                      <StageRowButton
                        icon="ArrowDown"
                        disabled={idx === stages.length - 1}
                        label={t('stages.moveDown')}
                        onClick={() => move(s, 1)}
                        testid={`manage-stage-down-${s.id}`}
                      />
                      <StageRowButton
                        icon="Pencil"
                        label={t('stages.rename')}
                        onClick={() => openEditor(s)}
                        testid={`manage-stage-edit-${s.id}`}
                      />
                      <StageRowButton
                        icon="Trash2"
                        label={t('stages.delete')}
                        destructive
                        onClick={() => setConfirmDelete(s)}
                        testid={`manage-stage-delete-${s.id}`}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-5 py-2">
                <TextField
                  ref={nameRef}
                  id="stage-name-input"
                  label={t('stages.nameLabel')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleSave();
                  }}
                  maxLength={60}
                  placeholder={t('stages.namePlaceholder')}
                  disabled={saving}
                  data-testid="stage-name-input"
                />
                <div className="flex flex-col gap-2">
                  <span className="text-caption text-muted-foreground">
                    {t('stages.colorLabel')}
                  </span>
                  <div className="grid grid-cols-6 gap-2">
                    {PALETTE_IDS.map((id) => {
                      const swatch = paletteColor(id);
                      const selected = color === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setColor(id)}
                          className="flex items-center justify-center transition-all duration-150"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 'var(--radius)',
                            backgroundColor: swatch.bg,
                            outline: selected ? '2px solid hsl(var(--action))' : 'none',
                            outlineOffset: selected ? '2px' : '0',
                          }}
                          aria-label={t('stages.colorOption', { n: id.slice(-2) })}
                          aria-pressed={selected}
                          data-testid={`stage-swatch-${id}`}
                          disabled={saving}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
      </DsModal>

      <ConfirmDialog
        open={confirmDelete !== null}
        title={t('stages.deleteConfirmTitle')}
        body={
          confirmDelete
            ? t('stages.deleteConfirmBody', {
                name: confirmDelete.name,
                count: agentCountByStage.get(confirmDelete.id) ?? 0,
              })
            : undefined
        }
        confirmLabel={t('stages.delete')}
        destructive
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          const target = confirmDelete;
          setConfirmDelete(null);
          if (target) void removeStage(target.id);
        }}
      />
    </>
  );
}

function StageRowButton({
  icon,
  label,
  onClick,
  disabled,
  destructive,
  testid,
}: {
  icon: 'ArrowUp' | 'ArrowDown' | 'Pencil' | 'Trash2';
  label: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  testid: string;
}) {
  return (
    <DsIconButton
      variant="standard"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={[
        'text-muted-foreground shrink-0',
        destructive ? 'hover:text-status-error' : 'hover:text-foreground',
      ].join(' ')}
      data-testid={testid}
    >
      <Icon name={icon} size={14} aria-hidden="true" />
    </DsIconButton>
  );
}
